/** Stable execution-provider identifiers and capability helpers.

IDs mirror the baking API. Capability flags below are a frontend fallback mirror
of server support states; the authoritative source is GET /api/v1/execution-providers/.
*/

export const EXECUTION_PROVIDERS = Object.freeze({
  UNITY: 'unity',
  MAYA: 'maya',
});

export const EXECUTION_PROVIDER_LABELS = Object.freeze({
  unity: 'Unity',
  maya: 'Maya',
});

/** Fallback capability map — must stay aligned with baking.providers definitions (gate off). */
export const EXECUTION_PROVIDER_CAPABILITIES = Object.freeze({
  unity: Object.freeze({
    id: 'unity',
    display_name: 'Unity',
    project_creation_enabled: true,
    revision_upload_enabled: true,
    static_analysis_enabled: true,
    job_creation_enabled: true,
    claiming_enabled: true,
    runtime_execution_enabled: true,
    runtime_artifacts_enabled: true,
    ui_flags: Object.freeze({
      uses_bake_terminology: true,
      show_job_controls: true,
    }),
  }),
  maya: Object.freeze({
    id: 'maya',
    display_name: 'Maya',
    project_creation_enabled: true,
    revision_upload_enabled: true,
    static_analysis_enabled: true,
    job_creation_enabled: false,
    claiming_enabled: false,
    runtime_execution_enabled: false,
    runtime_artifacts_enabled: false,
    runtime_unavailable_code: 'maya_execution_not_implemented',
    ui_flags: Object.freeze({
      uses_bake_terminology: false,
      show_job_controls: false,
      runtime_operation_label: 'Validate in Maya',
    }),
  }),
});

let _serverCapabilitiesById = null;

/**
 * Merge authoritative server capability payload into the client cache.
 * @param {{ providers?: Array<Record<string, unknown>> } | null} payload
 */
export function setServerProviderCapabilities(payload) {
  const map = {};
  const providers = (payload && payload.providers) || [];
  for (const entry of providers) {
    if (!entry || !entry.id) continue;
    map[String(entry.id).toLowerCase()] = Object.freeze({ ...entry });
  }
  _serverCapabilitiesById = Object.keys(map).length ? Object.freeze(map) : null;
}

export function clearServerProviderCapabilities() {
  _serverCapabilitiesById = null;
}

/**
 * Normalize an API provider value for display.
 * Unknown values are returned as-is (never silently labeled Unity).
 */
export function normalizeExecutionProvider(value) {
  if (value == null || value === '') {
    return null;
  }
  return String(value).trim().toLowerCase();
}

export function executionProviderLabel(value) {
  const key = normalizeExecutionProvider(value);
  if (!key) {
    return 'Unknown';
  }
  return EXECUTION_PROVIDER_LABELS[key] || `Unknown (${key})`;
}

export function isKnownExecutionProvider(value) {
  const key = normalizeExecutionProvider(value);
  return key === EXECUTION_PROVIDERS.UNITY || key === EXECUTION_PROVIDERS.MAYA;
}

export function getProviderCapabilities(value) {
  const key = normalizeExecutionProvider(value);
  if (_serverCapabilitiesById && key && _serverCapabilitiesById[key]) {
    return _serverCapabilitiesById[key];
  }
  if (!key || !EXECUTION_PROVIDER_CAPABILITIES[key]) {
    return Object.freeze({
      id: key || 'unknown',
      display_name: executionProviderLabel(value),
      project_creation_enabled: false,
      revision_upload_enabled: false,
      static_analysis_enabled: false,
      job_creation_enabled: false,
      claiming_enabled: false,
      runtime_execution_enabled: false,
      runtime_artifacts_enabled: false,
      ui_flags: Object.freeze({
        uses_bake_terminology: false,
        show_job_controls: false,
      }),
    });
  }
  return EXECUTION_PROVIDER_CAPABILITIES[key];
}

export function providerJobCreationEnabled(value) {
  return Boolean(getProviderCapabilities(value).job_creation_enabled);
}

export function providerRuntimeExecutionEnabled(value) {
  return Boolean(getProviderCapabilities(value).runtime_execution_enabled);
}

export function providerUsesBakeTerminology(value) {
  const caps = getProviderCapabilities(value);
  return Boolean(caps.ui_flags && caps.ui_flags.uses_bake_terminology);
}

export function providerRuntimeOperationLabel(value) {
  const caps = getProviderCapabilities(value);
  const flags = caps.ui_flags || {};
  return flags.runtime_operation_label || 'Run job';
}
